// PTZ 控制盤（plan/vms-web-v0.md LIVE-06）：方向 / 變焦 / 回原點 / 預置點 / 巡航（預置點之間輪流停留，cruise.ts）。
//   - 按住才轉：按下送一次、之後每秒續送（平台給閘道的 ContinuousMove 帶 2 秒 Timeout），放開 / 離開 / 視窗失焦送 stop；
//     關掉瀏覽器時沒人續送，攝影機 2 秒內自己停。
//   - lease：別人在控制 → 顯示是誰（角色）與剩幾秒；等級較高可以「搶控」；被搶走時提示。
//   - 攝影機沒有雲台（422 PTZ_UNSUPPORTED）→ 通知上層收起控制項（ONVIF-03：缺能力不顯示）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { ptzApi, type PtzAction, type PtzPreset } from '../../lib/planeApi';
import type { VmsDevice } from '../../lib/devices';
import { CRUISE_DWELL_DEFAULT, CRUISE_DWELL_MAX, CRUISE_DWELL_MIN, canCruise, clampDwell, nextPreset } from './cruise';

const RENEW_MS = 1000;
type Move = Extract<PtzAction, 'up' | 'down' | 'left' | 'right' | 'upleft' | 'upright' | 'downleft' | 'downright' | 'zoomin' | 'zoomout'>;
const PAD: (Move | 'home')[] = ['upleft', 'up', 'upright', 'left', 'home', 'right', 'downleft', 'down', 'downright'];
const ARROW: Record<string, string> = { upleft: '↖', up: '↑', upright: '↗', left: '←', home: '⌂', right: '→', downleft: '↙', down: '↓', downright: '↘' };

interface Blocked {
  code: 'PTZ_LEASE_HELD' | 'PTZ_PREEMPTED';
  role: string;
  expiresIn: number;
  canForce: boolean;
}

export function PtzPanel({ cam, onUnsupported, onClose }: { cam: VmsDevice; onUnsupported(): void; onClose(): void }) {
  const { t } = useTranslation();
  const [speed, setSpeed] = useState(50);
  const [presets, setPresets] = useState<PtzPreset[] | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preset, setPreset] = useState('');
  const [holding, setHolding] = useState<Move | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const force = useRef(false);
  const held = useRef(false); // 這個畫面拿過 lease（離開時才需要 release）
  // 巡航：cruiseAt = 目前停在哪個預置點（null = 沒在巡航）
  const [cruiseAt, setCruiseAt] = useState<string | null>(null);
  const [cruising, setCruising] = useState(false);
  const [dwell, setDwell] = useState(CRUISE_DWELL_DEFAULT);

  const send = useCallback(
    async (action: PtzAction, extra: { preset?: string; name?: string } = {}) => {
      try {
        const r = await ptzApi.command(cam.apiEndpoint, cam.homeId, { device_ref: cam.aid, action, speed, force: force.current, ...extra });
        force.current = false;
        held.current = action !== 'release';
        setBlocked(null);
        setErr(null);
        return r;
      } catch (e) {
        if (!(e instanceof KinioError)) {
          setErr(String(e));
          return null;
        }
        if (e.code === 'PTZ_UNSUPPORTED') onUnsupported();
        else if (e.status === 409 && (e.code === 'PTZ_LEASE_HELD' || e.code === 'PTZ_PREEMPTED')) {
          const b = e.detail ?? {};
          setBlocked({ code: e.code, role: String(b.holder_role ?? ''), expiresIn: Number(b.expires_in_seconds ?? 0), canForce: b.can_force === true });
          stopHold(false);
          setCruising(false); // 控制權不在我這裡：巡航停
        } else setErr(e.code);
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cam.aid, cam.apiEndpoint, cam.homeId, speed, onUnsupported],
  );

  const stopHold = (sendStop: boolean) => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
    setHolding((h) => {
      if (h && sendStop) void send('stop');
      return null;
    });
  };

  const startHold = (m: Move) => {
    setCruising(false); // 手動操作 = 巡航停
    stopHold(false);
    setHolding(m);
    void send(m);
    timer.current = setInterval(() => void send(m), RENEW_MS);
  };

  // 能力探測 + 預置點；離開時放掉 lease
  useEffect(() => {
    let alive = true;
    ptzApi
      .presets(cam.apiEndpoint, cam.homeId, cam.aid)
      .then((p) => alive && setPresets(p))
      .catch((e) => {
        if (!alive) return;
        if (e instanceof KinioError && e.code === 'PTZ_UNSUPPORTED') onUnsupported();
        else setPresets([]);
      });
    const blur = () => {
      stopHold(true);
      if (document.hidden) setCruising(false); // 分頁到背景：不佔著控制權繼續轉
    };
    window.addEventListener('blur', blur);
    document.addEventListener('visibilitychange', blur);
    return () => {
      alive = false;
      window.removeEventListener('blur', blur);
      document.removeEventListener('visibilitychange', blur);
      if (timer.current) clearInterval(timer.current);
      if (held.current) void ptzApi.command(cam.apiEndpoint, cam.homeId, { device_ref: cam.aid, action: 'release' }).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cam.aid, cam.apiEndpoint, cam.homeId]);

  // 巡航：開始時立刻去第一個，之後每 dwell 秒去下一個
  useEffect(() => {
    if (!cruising || !presets || !canCruise(presets)) {
      setCruiseAt(null);
      return;
    }
    let cur: string | null = null;
    const step = () => {
      const p = nextPreset(presets, cur);
      if (!p) return;
      cur = p.token;
      setCruiseAt(p.token);
      void send('preset_goto', { preset: p.token });
    };
    step();
    const iv = setInterval(step, clampDwell(dwell) * 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cruising, presets, dwell]);

  const holdProps = (m: Move) => ({
    onPointerDown: (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      startHold(m);
    },
    onPointerUp: () => stopHold(true),
    onPointerCancel: () => stopHold(true),
    onLostPointerCapture: () => stopHold(true),
    onKeyDown: (e: React.KeyboardEvent) => {
      if ((e.key === ' ' || e.key === 'Enter') && !holding) startHold(m);
    },
    onKeyUp: (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') stopHold(true);
    },
    'aria-pressed': holding === m,
  });

  const savePreset = async () => {
    const name = window.prompt(t('ptz.preset_name'));
    if (!name) return;
    const r = await send('preset_set', { name });
    if (r?.preset) setPresets(await ptzApi.presets(cam.apiEndpoint, cam.homeId, cam.aid).catch(() => presets ?? []));
  };

  return (
    <div className="ptz-panel" role="group" aria-label={t('ptz.title')} onClick={(e) => e.stopPropagation()}>
      <div className="ptz-head">
        <strong>{t('ptz.title')}</strong>
        <span className="spacer" />
        <button className="icon-btn" aria-label={t('common.close')} onClick={onClose}>
          ✕
        </button>
      </div>
      {blocked && (
        <div className="ptz-blocked" role="status">
          <span>
            {blocked.code === 'PTZ_PREEMPTED'
              ? t('ptz.preempted', { role: t(`ptz.role_${blocked.role}`, { defaultValue: blocked.role }) })
              : t('ptz.held', { role: t(`ptz.role_${blocked.role}`, { defaultValue: blocked.role }), n: blocked.expiresIn })}
          </span>
          {blocked.canForce && (
            <button
              className="btn btn-sm"
              onClick={() => {
                force.current = true;
                void send('stop');
              }}
            >
              {t('ptz.take_over')}
            </button>
          )}
        </div>
      )}
      {err && <p className="error small">{t(`ptz.err_${err}`, { defaultValue: t('common.op_failed', { code: err }) })}</p>}
      <div className="ptz-body">
        <div className="ptz-pad">
          {PAD.map((a) =>
            a === 'home' ? (
              <button key={a} className="ptz-btn" title={t('ptz.home')} aria-label={t('ptz.home')} onClick={() => void send('home')}>
                {ARROW[a]}
              </button>
            ) : (
              <button key={a} className="ptz-btn" title={t(`ptz.dir_${a}`)} aria-label={t(`ptz.dir_${a}`)} {...holdProps(a)}>
                {ARROW[a]}
              </button>
            ),
          )}
        </div>
        <div className="ptz-side">
          <div className="ptz-zoom">
            <button className="ptz-btn" aria-label={t('ptz.zoom_in')} title={t('ptz.zoom_in')} {...holdProps('zoomin')}>
              ＋
            </button>
            <button className="ptz-btn" aria-label={t('ptz.zoom_out')} title={t('ptz.zoom_out')} {...holdProps('zoomout')}>
              －
            </button>
          </div>
          <label className="small">
            {t('ptz.speed')}
            <input type="range" min={10} max={100} step={10} value={speed} onChange={(e) => setSpeed(Number(e.target.value))} aria-label={t('ptz.speed')} />
          </label>
        </div>
      </div>
      <div className="ptz-presets">
        <select className="input" value={preset} onChange={(e) => setPreset(e.target.value)} aria-label={t('ptz.presets')} disabled={!presets?.length}>
          <option value="">{presets === null ? t('ptz.loading') : presets.length ? t('ptz.pick_preset') : t('ptz.no_presets')}</option>
          {presets?.map((p) => (
            <option key={p.token} value={p.token}>
              {p.name || p.token}
            </option>
          ))}
        </select>
        <button
          className="btn btn-sm"
          disabled={!preset}
          onClick={() => {
            setCruising(false);
            void send('preset_goto', { preset });
          }}
        >
          {t('ptz.goto')}
        </button>
        <button className="btn btn-sm" onClick={() => void savePreset()}>
          {t('ptz.save_preset')}
        </button>
      </div>
      {canCruise(presets) && (
        <div className="ptz-presets ptz-cruise" data-cruising={cruising ? '1' : undefined}>
          <button className="btn btn-sm" aria-pressed={cruising} onClick={() => setCruising((c) => !c)}>
            {cruising ? t('ptz.cruise_stop') : t('ptz.cruise_start')}
          </button>
          <label className="small">
            {t('ptz.cruise_dwell')}
            <input
              className="input"
              type="number"
              min={CRUISE_DWELL_MIN}
              max={CRUISE_DWELL_MAX}
              value={dwell}
              style={{ width: 64 }}
              aria-label={t('ptz.cruise_dwell')}
              onChange={(e) => setDwell(Number(e.target.value))}
              onBlur={() => setDwell((d) => clampDwell(d))}
            />
          </label>
          {cruising && cruiseAt && (
            <span className="small muted" data-testid="cruise-at">
              {t('ptz.cruise_at', { name: presets?.find((p) => p.token === cruiseAt)?.name || cruiseAt })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
